/**
 * quality_tradeoff.ts — THE LOADOUT'S QUALITY TRADEOFF (space campaign J, 2026-09-30; report §17.4). Pure.
 *
 * A higher-quality component is not a free upgrade: `shipStats` lifts the slot's stat by quality (engine speed
 * 1 + 0.35 q/1000 …) and `fitCost` lifts its draw by base × (0.7 + 0.6 q/1000) on BOTH cpu and powergrid. A q800 engine
 * vs a q400 one: speed ×1.28 vs ×1.14 (+12.3 %) for draw ×1.18 vs ×0.94 (+25.5 %). The Loadout shows both outcomes
 * before a swap — through the game's own functions, never a second formula.
 */
import { shipStats, type ShipStats } from './fleet.js';
import { fitCost } from '@shared/space_industry.js';

/** The stat each slot's quality moves, read off shipStats (label, key, and how to print it). */
const SLOT_STAT: Record<string, { label: string; key: keyof ShipStats; fmt: (v: number) => string }> = {
  engine: { label: 'speed', key: 'maxSpeed', fmt: (v) => `${Math.round(v)} m/s` },
  armor: { label: 'hull', key: 'hp', fmt: (v) => `${Math.round(v)}` },
  shield: { label: 'shield', key: 'shield', fmt: (v) => `${Math.round(v)}` },
  capacitor: { label: 'capacitor', key: 'capacitor', fmt: (v) => `${Math.round(v)}` },
  weapon: { label: 'damage', key: 'weaponMult', fmt: (v) => `×${v.toFixed(2)}` },
  reactor: { label: 'regen', key: 'regenMult', fmt: (v) => `×${v.toFixed(2)}` },
  cargo: { label: 'cargo', key: 'cargo', fmt: (v) => `${Math.round(v)}` },
  missile: { label: 'missiles', key: 'missiles', fmt: (v) => `${Math.round(v)}` },
  counter: { label: 'counters', key: 'counters', fmt: (v) => `${Math.round(v)}` },
  droidport: { label: 'droid', key: 'droid', fmt: (v) => v.toFixed(2) },
};
/** The draw multiplier of a quality (fitCost's own m, unrounded). */
export function drawMult(quality: number): number { return 0.7 + Math.max(0, Math.min(1000, quality)) / 1000 * 0.6; }

export interface Tradeoff {
  slot: string; stat: string;
  newVal: number; oldVal: number | null; baseVal: number;
  /** The stat relative to the chassis with the slot empty (×). */
  newMult: number; oldMult: number | null;
  gainPct: number | null;
  drawNew: { cpu: number; pg: number; mult: number };
  drawOld: { cpu: number; pg: number; mult: number } | null;
  drawPct: number | null;
  line: string;
}
const pct = (a: number, b: number): number => (b === 0 ? 0 : (a / b - 1) * 100);
const sign = (x: number): string => `${x >= 0 ? '+' : ''}${x.toFixed(1)} %`;

/** Compare a candidate's quality with what is fitted in that slot now (null = the slot is empty). */
export function qualityTradeoff(chassisKey: string, slot: string, itemKey: string, qNew: number, qOld: number | null): Tradeoff | null {
  const s = SLOT_STAT[slot];
  if (!s) return null;
  const base = Number(shipStats(chassisKey, [])[s.key]);
  const nv = Number(shipStats(chassisKey, [{ slot, itemKey, quality: qNew }])[s.key]);
  const ov = qOld === null ? null : Number(shipStats(chassisKey, [{ slot, itemKey, quality: qOld }])[s.key]);
  const dn = { ...fitCost(slot, qNew), mult: drawMult(qNew) };
  const dov = qOld === null ? null : { ...fitCost(slot, qOld), mult: drawMult(qOld) };
  const newMult = base ? nv / base : 1, oldMult = ov === null ? null : base ? ov / base : 1;
  const gainPct = ov === null ? null : pct(nv, ov);
  const drawPct = dov === null ? null : pct(dn.mult, dov.mult);
  const line = ov === null || dov === null
    ? `${s.label} ${s.fmt(nv)} (empty slot: ${s.fmt(base)}) · draws ${dn.cpu} cpu / ${dn.pg} pg (×${dn.mult.toFixed(2)} base)`
    : `vs fitted q${qOld}: ${s.label} ${s.fmt(nv)} vs ${s.fmt(ov)} (${sign(gainPct!)}) · draw ×${dn.mult.toFixed(2)} vs ×${dov.mult.toFixed(2)} base (${sign(drawPct!)}: ${dn.cpu}/${dn.pg} vs ${dov.cpu}/${dov.pg} cpu/pg)`;
  return { slot, stat: s.label, newVal: nv, oldVal: ov, baseVal: base, newMult, oldMult, gainPct, drawNew: dn, drawOld: dov, drawPct, line };
}
